// src/components/GuestDashboard.js (FINAL AND CORRECTED)
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    doc, 
    updateDoc,
    // CORRECTED IMPORTS:
    addDoc, 
    serverTimestamp 
} from 'firebase/firestore'; 
import { useAuth } from '../context/AuthContext';
import Chat from './Chat'; // Import the shared Chat component

export default function GuestDashboard() {
    const { user, loading: authLoading } = useAuth();
    const [guestData, setGuestData] = useState(null);
    const [chatId, setChatId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const guestId = user?.uid;
    const hotelId = user?.hotelId;
    const { t } = useTranslation();
    useEffect(() => {
        if (authLoading || !guestId || !hotelId) return;
        
        const fetchData = async () => {
            setLoading(true);
            setError('');
            try {
                // 1. Fetch Guest Data (The guest's document tied to the Firebase Auth UID)
                const guestsQuery = query(collection(db, 'guests'), where('guestId', '==', guestId), where('status', '!=', 'CHECKED_OUT_FINALIZED'));
                const guestSnap = await getDocs(guestsQuery);

                if (guestSnap.empty) {
                    setError("No active check-in found for this account.");
                    setLoading(false);
                    return;
                }
                const currentGuest = { id: guestSnap.docs[0].id, ...guestSnap.docs[0].data() };
                setGuestData(currentGuest);
                
                // 2. Find/Create Chat Room
                const chatQuery = query(collection(db, 'chats'), where('guestId', '==', currentGuest.id), where('hotelId', '==', hotelId));
                const chatSnap = await getDocs(chatQuery);
                
                if (chatSnap.empty) {
                    // Create chat if it doesn't exist yet (e.g., if guest starts the conversation first)
                    const chatRef = await addDoc(collection(db, "chats"), {
                        hotelId: hotelId,
                        guestId: currentGuest.id,
                        guestName: currentGuest.fullName,
                        roomNumber: currentGuest.roomNumber,
                        lastMessageText: 'New chat started by guest.',
                        lastMessageTimestamp: serverTimestamp(),
                    });
                    setChatId(chatRef.id);
                } else {
                    setChatId(chatSnap.docs[0].id);
                }

            } catch (err) {
                console.error("Error fetching guest data or chat:", err);
                setError("Failed to load dashboard data. Check network and Firebase rules.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user, authLoading, guestId, hotelId]);

    // Function to handle guest's request to clean (Guest-Initiated)
    const handleRequestCleaning = async () => {
        if (!guestData || !chatId) return;
        
        if (!window.confirm("Confirm: Send a cleaning request to the staff now?")) return;

        try {
            // 1. Update Guest document to track the request
            await updateDoc(doc(db, 'guests', guestData.id), {
                cleaningRequested: true,
                cleaningRequestTime: new Date().toISOString()
            });

            // 2. Send an automatic message to the chat
            const requestText = `🧹 Guest ${guestData.roomNumber} is requesting cleaning service now.`;
            await addDoc(collection(db, "messages"), {
                chatId: chatId,
                senderId: guestId, // Sender is the Firebase Auth UID
                senderRole: 'guest',
                text: requestText,
                timestamp: serverTimestamp(),
            });

            setError('');
            alert("Cleaning request sent! Staff will respond via chat shortly.");
        } catch (err) {
            console.error("Error sending cleaning request:", err);
            setError("Failed to send cleaning request.");
        }
    }
    
    // Function to handle guest's request to checkout
    const handleRequestCheckout = async () => {
        if (!guestData) return;
        
        if (!window.confirm("Confirm: Request check-out and invoice preparation?")) return;

        try {
            await updateDoc(doc(db, 'guests', guestData.id), {
                status: 'CHECKOUT_REQUESTED'
            });
            alert("Check-out requested! Staff will finalize your bill shortly.");
        } catch (err) {
            console.error("Error requesting checkout:", err);
            setError("Failed to request checkout.");
        }
    }


    if (authLoading || loading) return <div className="container" style={{paddingTop: '50px'}}><div className="card">Loading Guest Dashboard...</div></div>;
    
    if (error) return <div className="container" style={{paddingTop: '50px'}}><div className="card error-message">{error}</div></div>;


    return (
        <div className="container" style={{paddingTop: '50px'}}>
            <h2>Welcome, {guestData?.fullName}!</h2>
            <div className="card" style={{padding: '20px', backgroundColor: 'var(--color-background)', borderLeft: '5px solid var(--color-primary)'}}>
                <h3 style={{margin: 0}}>Your Room: {guestData?.roomNumber}</h3>
                <p>Check-out Date: **{new Date(guestData?.checkOutDate).toLocaleDateString()}**</p>
                <p>Status: **{guestData?.status.replace(/_/g, ' ').toUpperCase()}**</p>
                <div>
            <h1>{t('guest_dash.title')}</h1>
            <button onClick={handleRequestCleaning}>{t('guest_dash.req_cleaning')}</button>
            <div className="section-title">{t('guest_dash.chat_title')}</div>
            {/* Remplacez tous les textes par t('guest_dash.clé') */}
        </div>
                <div style={{display: 'flex', gap: '15px', marginTop: '15px'}}>
                    <button 
                        onClick={handleRequestCleaning} 
                        style={{backgroundColor: 'var(--color-accent)', color: 'white', padding: '10px 15px', border: 'none', borderRadius: '5px'}}
                    >
                        ⭐ Request Cleaning Now
                    </button>

                    <button 
                        onClick={handleRequestCheckout} 
                        disabled={guestData?.status === 'CHECKOUT_REQUESTED'}
                        style={{backgroundColor: 'var(--color-error)', color: 'white', padding: '10px 15px', border: 'none', borderRadius: '5px'}}
                    >
                        Request Check-out
                    </button>
                </div>
            </div>

            {/* Guest Chat Window */}
            <div className="section-title" style={{marginTop: '40px'}}>💬 Staff Communication</div>
            <div style={{height: '500px'}}>
                {chatId ? (
                    <Chat 
                        currentChatId={chatId} 
                        isStaff={false} // CRITICAL: Identify as guest
                        hotelId={hotelId} 
                        guestId={guestData.id} // The Firebase Guest document ID
                    />
                ) : (
                    <div className="card" style={{textAlign: 'center', padding: '40px'}}>Starting conversation...</div>
                )}
            </div>
        </div>
    );
}